import { useCallback, useMemo, useState } from "react";
import { Banner, Flex, QueryLoader } from "@jsq/ds/core";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import UploadModal from "../Documents/UploadModal";
import { CrsOverviewSection } from "./CrsOverviewSection";
import { FatcaOverviewSection } from "./FatcaOverviewSection";
import { pageContainer } from "./styles_vanilla.css";
import { TaxPersonsModal } from "./TaxPersonsModal";
import { useStandaloneFatcaCrsData } from "./useStandaloneFatcaCrsData";
import {
  buildTaxFormUrlForDocument,
  type ComplianceCaseData,
  type ComplianceCaseDocument,
  controllingPersonToTaxPerson,
  type CrsOverviewRow,
  type FatcaOverviewRow,
  type SourceDocumentInfo,
  type TaxOverviewVariant,
} from "./utils";

type ActiveModal =
  { kind: "fatca"; row: FatcaOverviewRow } | { kind: "crs"; row: CrsOverviewRow } | null;

const findDocument = (
  documents: ComplianceCaseDocument[],
  source: SourceDocumentInfo | null,
): ComplianceCaseDocument | null =>
  source ? (documents.find((d) => d.id === source.documentId) ?? null) : null;

export type StandaloneFatcaCrsProps = {
  complianceCase: ComplianceCaseData | null;
  readonly?: boolean;
};

export const StandaloneFatcaCrs = ({
  complianceCase,
  readonly = false,
}: StandaloneFatcaCrsProps) => {
  const { caseId } = useCurrentCaseContext();

  const { fatcaRow, crsRows, queryResults, assignmentsError } = useStandaloneFatcaCrsData({
    caseId,
    complianceCase,
  });

  const [showUpload, setShowUpload] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const handleAddDocument = useCallback(() => setShowUpload(true), []);
  const handleCloseUpload = useCallback(() => setShowUpload(false), []);
  const handleCloseModal = useCallback(() => setActiveModal(null), []);
  const handleOpenFatcaPersons = useCallback(
    (row: FatcaOverviewRow) => setActiveModal({ kind: "fatca", row }),
    [],
  );
  const handleOpenCrsPersons = useCallback(
    (row: CrsOverviewRow) => setActiveModal({ kind: "crs", row }),
    [],
  );

  const fatcaRows = fatcaRow ? [fatcaRow] : [];

  const modalProps = useMemo(() => {
    if (!activeModal || !complianceCase?.investingProfile) return null;
    const variant: TaxOverviewVariant = activeModal.kind;
    const document = findDocument(
      complianceCase.documents ?? [],
      activeModal.row.sourceDocument,
    );
    return {
      variant,
      persons: activeModal.row.persons.map(controllingPersonToTaxPerson),
      editUrl: buildTaxFormUrlForDocument(caseId, document, complianceCase.investingProfile.id),
      subtitle:
        activeModal.kind === "fatca"
          ? `All positions for ${activeModal.row.rootProfileName}`
          : activeModal.row.positionLabel,
    };
  }, [activeModal, complianceCase, caseId]);

  return (
    <QueryLoader result={queryResults}>
      <Flex flexDirection="column" className={pageContainer}>
        {assignmentsError && (
          <Banner
            variant="danger"
            title="Unable to load tax document assignments"
            description={assignmentsError.message}
          />
        )}
        <FatcaOverviewSection
          caseId={caseId}
          rows={fatcaRows}
          onAddDocument={handleAddDocument}
          onOpenPersons={handleOpenFatcaPersons}
          readonly={readonly}
        />
        <CrsOverviewSection
          caseId={caseId}
          rows={crsRows}
          onAddDocument={handleAddDocument}
          onOpenPersons={handleOpenCrsPersons}
          readonly={readonly}
        />
      </Flex>
      <UploadModal showModal={showUpload} onClose={handleCloseUpload} caseId={caseId} />
      {modalProps && (
        <TaxPersonsModal
          show
          variant={modalProps.variant}
          subtitle={modalProps.subtitle}
          persons={modalProps.persons}
          editUrl={modalProps.editUrl}
          onClose={handleCloseModal}
          readonly={readonly}
        />
      )}
    </QueryLoader>
  );
};
